import React from 'react';
import {
  Alert,
  AlertActionLink,
  Bullseye,
  Button,
  Content,
  Flex,
  FlexItem,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Label,
  LabelGroup,
  Spinner,
  TextInput,
  Title,
  ToggleGroup,
  ToggleGroupItem,
} from '@patternfly/react-core';
import { PlusCircleIcon, TrashIcon } from '@patternfly/react-icons';
import { MAX_NAMESERVERS, MIN_NAMESERVERS, nameserverIssues } from './types';
import type { NsMode } from './types';
import type { DnsEndpoint } from '../../../../types/models';

interface NameserversStepProps {
  mode: NsMode;
  onModeChange: (mode: NsMode) => void;
  // Managed endpoint
  endpoints: DnsEndpoint[];
  endpointsLoading: boolean;
  endpointsError: string | null;
  onRetryEndpoints: () => void;
  endpointId: number | null;
  onEndpointChange: (id: number | null) => void;
  // Manual hostnames
  hostnames: string[];
  onHostnamesChange: (hostnames: string[]) => void;
}

const NameserversStep: React.FunctionComponent<NameserversStepProps> = ({
  mode,
  onModeChange,
  endpoints,
  endpointsLoading,
  endpointsError,
  onRetryEndpoints,
  endpointId,
  onEndpointChange,
  hostnames,
  onHostnamesChange,
}) => {
  const { rowErrors, formError } = nameserverIssues(hostnames);
  const selectedEndpoint = endpointId !== null ? endpoints.find((e) => e.id === endpointId) ?? null : null;

  const updateRow = (index: number, value: string) => {
    const next = [...hostnames];
    next[index] = value;
    onHostnamesChange(next);
  };
  const addRow = () => onHostnamesChange([...hostnames, '']);
  const removeRow = (index: number) => {
    if (hostnames.length <= MIN_NAMESERVERS) {
      return;
    }
    onHostnamesChange(hostnames.filter((_, i) => i !== index));
  };

  const renderEndpointMode = () => {
    if (endpointsLoading) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading DNS endpoints" />
        </Bullseye>
      );
    }
    if (endpointsError) {
      return (
        <Alert
          variant="warning"
          isInline
          title="Could not load managed DNS endpoints"
          actionLinks={<AlertActionLink onClick={onRetryEndpoints}>Retry</AlertActionLink>}
        >
          {endpointsError} You can switch to entering nameservers manually instead.
        </Alert>
      );
    }
    if (endpoints.length === 0) {
      return (
        <Alert variant="info" isInline isPlain title="No managed DNS endpoints are available.">
          Switch to entering nameservers manually.
        </Alert>
      );
    }
    return (
      <FormGroup label="Managed DNS endpoint" fieldId="ns-endpoint" isRequired>
        <FormSelect
          id="ns-endpoint"
          value={endpointId !== null ? String(endpointId) : ''}
          onChange={(_event, value) => onEndpointChange(value === '' ? null : Number(value))}
          aria-label="Select a managed DNS endpoint"
        >
          <FormSelectOption value="" label="Select a managed DNS endpoint" isPlaceholder />
          {endpoints.map((ep) => (
            <FormSelectOption key={ep.id} value={String(ep.id)} label={ep.name} />
          ))}
        </FormSelect>
        {selectedEndpoint && (
          <FormHelperText>
            <HelperText>
              <HelperTextItem>
                {selectedEndpoint.public_nameservers.length > 0 ? (
                  <>
                    These nameservers will be used:
                    <LabelGroup numLabels={10} className="pf-v6-u-ml-sm" aria-label="Endpoint nameservers">
                      {selectedEndpoint.public_nameservers.map((ns) => (
                        <Label key={ns} isCompact>
                          {ns}
                        </Label>
                      ))}
                    </LabelGroup>
                  </>
                ) : (
                  'This endpoint has no published nameservers — pick another, or enter nameservers manually.'
                )}
              </HelperTextItem>
            </HelperText>
          </FormHelperText>
        )}
      </FormGroup>
    );
  };

  const renderHostnamesMode = () => (
    <FormGroup label="Nameservers" fieldId="ns-hostnames" isRequired>
      <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsSm' }}>
        {hostnames.map((host, index) => (
          <FlexItem key={index}>
            <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsFlexStart' }}>
              <FlexItem grow={{ default: 'grow' }}>
                <TextInput
                  id={`ns-host-${index}`}
                  type="text"
                  aria-label={`Nameserver ${index + 1} hostname`}
                  placeholder={`ns${index + 1}.example.com`}
                  value={host}
                  onChange={(_event, value) => updateRow(index, value)}
                  validated={rowErrors[index] ? 'error' : 'default'}
                />
                {rowErrors[index] && (
                  <FormHelperText>
                    <HelperText>
                      <HelperTextItem variant="error">{rowErrors[index]}</HelperTextItem>
                    </HelperText>
                  </FormHelperText>
                )}
              </FlexItem>
              <FlexItem>
                <Button
                  variant="plain"
                  icon={<TrashIcon />}
                  aria-label={`Remove nameserver ${index + 1}`}
                  isDisabled={hostnames.length <= MIN_NAMESERVERS}
                  onClick={() => removeRow(index)}
                />
              </FlexItem>
            </Flex>
          </FlexItem>
        ))}
      </Flex>
      <Button
        variant="link"
        icon={<PlusCircleIcon />}
        onClick={addRow}
        isDisabled={hostnames.length >= MAX_NAMESERVERS}
        className="pf-v6-u-mt-sm"
      >
        Add nameserver
      </Button>
      {formError && (
        <FormHelperText>
          <HelperText>
            <HelperTextItem variant="error">{formError}</HelperTextItem>
          </HelperText>
        </FormHelperText>
      )}
    </FormGroup>
  );

  return (
    <Flex direction={{ default: 'column' }} spaceItems={{ default: 'spaceItemsLg' }}>
      <FlexItem>
        <Title headingLevel="h2" size="lg">
          Nameservers
        </Title>
        <Content component="p">
          These nameservers will be set for <strong>every</strong> domain in this order.
        </Content>
      </FlexItem>

      <FlexItem>
        <ToggleGroup aria-label="Choose how to provide nameservers">
          <ToggleGroupItem
            text="Use a managed DNS endpoint"
            buttonId="ns-mode-endpoint"
            isSelected={mode === 'endpoint'}
            onChange={() => onModeChange('endpoint')}
          />
          <ToggleGroupItem
            text="Enter nameservers manually"
            buttonId="ns-mode-hostnames"
            isSelected={mode === 'hostnames'}
            onChange={() => onModeChange('hostnames')}
          />
        </ToggleGroup>
      </FlexItem>

      <FlexItem>{mode === 'endpoint' ? renderEndpointMode() : renderHostnamesMode()}</FlexItem>
    </Flex>
  );
};

export default NameserversStep;
